<!-- Estrutura padrão dos dialogs do design-system: cabeçalho, conteúdo e rodapé. -->
<aionz-header-bottom-sheet
  aionz-dialog-title
  (onClose)="close()">
  Adicionar fornecedor à cotação
</aionz-header-bottom-sheet>

<aionz-dialog-content class="new-supplier-content">
  <p class="dialog-subtitle">
    Comece pelo CNPJ: se o fornecedor já estiver cadastrado, os dados são preenchidos sozinhos.
  </p>

  <!-- O botão de envio fica no rodapé, fora do form: ligado a ele pelo atributo form. -->
  <form
    id="new-supplier-form"
    class="form-content"
    [formGroup]="form"
    (ngSubmit)="submit()">
    <!-- Ordem: CNPJ (dispara a busca) → nome → contato → IE opcional. Rótulos dentro dos campos. -->
    <mat-form-field
      appearance="outline"
      subscriptSizing="dynamic">
      <mat-label>CNPJ</mat-label>
      <input
        matInput
        placeholder="00.000.000/0000-00"
        formControlName="cnpj"
        cdkFocusInitial
        name="cnpj"
        autocomplete="off"
        spellcheck="false"
        [mask]="masks.cnpj"
        class="uppercase-input"
        [dropSpecialCharacters]="false" />
      @if (isLoading()) {
        <mat-spinner
          matSuffix
          [diameter]="20"></mat-spinner>
      }
      @if (cnpjLookup() !== 'idle') {
        <mat-hint
          class="cnpj-lookup"
          [class.cnpj-lookup--found]="cnpjLookup() === 'found'"
          aria-live="polite">
          @switch (cnpjLookup()) {
            @case ('searching') {
              Buscando fornecedor…
            }
            @case ('found') {
              <mat-icon aria-hidden="true">check_circle</mat-icon>
              Fornecedor encontrado: dados preenchidos.
              @if (alreadyInAllItems()) {
                Já está em todos os itens desta solicitação.
              } @else if (presentProductIds().size > 0) {
                Já está em {{ presentProductIds().size }} de {{ items.length }} itens.
              }
              @if (suppliedProductIds().size > 0 && !alreadyInAllItems()) {
                Já forneceu {{ suppliedProductIds().size }}
                {{ suppliedProductIds().size === 1 ? 'item' : 'itens' }} desta solicitação.
              }
            }
            @case ('notFound') {
              <mat-icon aria-hidden="true">info</mat-icon>
              CNPJ não encontrado: preencha os dados abaixo.
            }
          }
        </mat-hint>
      }
      <mat-error>{{ getErrorMessage('cnpj') }}</mat-error>
    </mat-form-field>

    <mat-form-field
      appearance="outline"
      subscriptSizing="dynamic">
      <mat-label>Nome</mat-label>
      <input
        matInput
        placeholder="Razão social ou nome fantasia"
        formControlName="name"
        name="organization"
        autocomplete="organization" />
      <mat-error>{{ getErrorMessage('name') }}</mat-error>
    </mat-form-field>

    <div class="form-row">
      <mat-form-field
        appearance="outline"
        subscriptSizing="dynamic">
        <mat-label>E-mail</mat-label>
        <input
          matInput
          type="email"
          inputmode="email"
          placeholder="exemplo@email.com"
          formControlName="email"
          name="email"
          autocomplete="email"
          spellcheck="false" />
        <mat-hint>Recebe o convite para cotar.</mat-hint>
        <mat-error>{{ getErrorMessage('email') }}</mat-error>
      </mat-form-field>

      <mat-form-field
        appearance="outline"
        subscriptSizing="dynamic">
        <mat-label>Telefone</mat-label>
        <input
          matInput
          type="tel"
          inputmode="tel"
          placeholder="(00) 00000-0000"
          formControlName="phone"
          name="tel"
          autocomplete="tel"
          [mask]="masks.phone"
          [dropSpecialCharacters]="false" />
        <mat-error>{{ getErrorMessage('phone') }}</mat-error>
      </mat-form-field>
    </div>

    <mat-form-field
      appearance="outline"
      subscriptSizing="dynamic">
      <mat-label>Inscrição estadual (opcional)</mat-label>
      <input
        matInput
        placeholder="Digite a inscrição estadual"
        formControlName="stateRegistration"
        name="stateRegistration"
        autocomplete="off"
        spellcheck="false" />
    </mat-form-field>

    @if (canChooseItems && !alreadyInAllItems()) {
      <!-- Para quais itens: por padrão todos; "Escolher itens" convida só para os marcados. -->
      <fieldset class="scope">
        <legend class="scope-legend">Convidar para</legend>
        <mat-radio-group
          class="scope-options"
          aria-label="Convidar para"
          [value]="scope()"
          [disabled]="saving()"
          (change)="scope.set($event.value)">
          <mat-radio-button value="all">
            {{ presentProductIds().size > 0 ? 'Todos em que ainda não está' : 'Todos os itens' }}
            ({{ availableItems().length }})
          </mat-radio-button>
          <mat-radio-button value="some">Escolher itens</mat-radio-button>
        </mat-radio-group>
        @if (suppliedProductIds().size > 0) {
          <button
            mat-stroked-button
            type="button"
            class="supplied-shortcut"
            [disabled]="saving()"
            (click)="selectSuppliedItems()">
            <mat-icon aria-hidden="true">history</mat-icon>
            Marcar os que ele já forneceu ({{ suppliedProductIds().size }})
          </button>
        }

        @if (scope() === 'some') {
          <div class="items-toolbar">
            <span aria-live="polite">{{ selectedProductIds().size }} de {{ availableItems().length }} selecionados</span>
            <span class="items-toolbar-actions">
              <button
                mat-button
                type="button"
                [disabled]="saving()"
                (click)="selectAllItems()">
                Marcar todos
              </button>
              <button
                mat-button
                type="button"
                [disabled]="saving() || selectedProductIds().size === 0"
                (click)="clearItems()">
                Limpar
              </button>
            </span>
          </div>
          <ul
            class="items-list"
            aria-label="Itens da solicitação">
            @for (item of items; track item.productId) {
              <li>
                <mat-checkbox
                  [checked]="selectedProductIds().has(item.productId) || presentProductIds().has(item.productId)"
                  [disabled]="saving() || presentProductIds().has(item.productId)"
                  (change)="toggleItem(item.productId, $event.checked)">
                  <span class="item-line">
                    <span class="item-code">{{ item.code }}</span>
                    <span
                      class="item-name"
                      [title]="item.product"
                      >{{ item.product }}</span
                    >
                    @if (presentProductIds().has(item.productId)) {
                      <span class="item-present">Já está neste item</span>
                    } @else if (suppliedProductIds().has(item.productId)) {
                      <span class="item-supplied">Já forneceu</span>
                    }
                    @if (item.branch) {
                      <span
                        class="item-branch"
                        [title]="'Local de entrega: ' + item.branch">
                        <mat-icon aria-hidden="true">location_on</mat-icon>{{ item.branch }}
                      </span>
                    }
                  </span>
                </mat-checkbox>
              </li>
            }
          </ul>
        }
      </fieldset>
    }
  </form>
</aionz-dialog-content>

<aionz-dialog-actions
  align="end"
  class="dialog-footer">
  <p
    class="scope-note"
    [class.scope-note--warning]="missingItems()">
    <mat-icon aria-hidden="true">info</mat-icon>
    {{ scopeNote() }}
  </p>
  <button
    mat-flat-button
    color="primary"
    type="submit"
    form="new-supplier-form"
    [disabled]="!form.valid || saving() || missingItems()">
    @if (saving()) {
      <mat-spinner
        class="button-spinner"
        [diameter]="16"
        aria-hidden="true"></mat-spinner>
      @if (savingProgress(); as p) {
        Adicionando {{ p.done }} de {{ p.total }}…
      } @else {
        Adicionando…
      }
    } @else {
      Adicionar fornecedor para cotação
    }
  </button>
</aionz-dialog-actions>
